import { useEffect, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { JALALI_MONTHS, WEEKDAYS_SHORT, fa, fmtDate, fmtTime, jalaliMonthLength, jalaliOf, jalaliToDate, weekdayIndex, sameDay } from "./jalali";

interface Props { value: Date | null; onChange: (d: Date) => void; withTime?: boolean; placeholder?: string; min?: Date }
const p2 = (n: number) => String(n).padStart(2, "0");

/** انتخاب تاریخ و ساعت شمسی — پنل داخل صفحه باز می‌شود (بدون بریده‌شدن در مودال) */
export default function JalaliPicker({ value, onChange, withTime = true, placeholder = "انتخاب تاریخ", min }: Props) {
  const [open, setOpen] = useState(false);
  const base = value ?? new Date();
  const [vy, setVy] = useState(jalaliOf(base).jy);
  const [vm, setVm] = useState(jalaliOf(base).jm);
  useEffect(() => { if (open) { const j = jalaliOf(value ?? new Date()); setVy(j.jy); setVm(j.jm); } }, [open]); // eslint-disable-line

  const today = new Date();
  const first = weekdayIndex(jalaliToDate(vy, vm, 1));
  const len = jalaliMonthLength(vy, vm);
  const step = (n: number) => { let m = vm + n, y = vy; if (m > 12) { m = 1; y++; } if (m < 1) { m = 12; y--; } setVm(m); setVy(y); };
  const keepTime = () => ({ h: value ? value.getHours() : 9, m: value ? value.getMinutes() : 0 });
  const pick = (d: number) => { const t = keepTime(); onChange(jalaliToDate(vy, vm, d, t.h, t.m)); if (!withTime) setOpen(false); };
  const quick = (addDays: number) => { const t = keepTime(); const d = new Date(); d.setDate(d.getDate() + addDays); d.setHours(t.h, t.m, 0, 0); onChange(d); const j = jalaliOf(d); setVy(j.jy); setVm(j.jm); };
  const setTime = (s: string) => { if (!s) return; const [h, m] = s.split(":").map(Number); const d = new Date(value ?? new Date()); d.setHours(h, m, 0, 0); onChange(d); };
  const isDisabled = (d: number) => !!min && jalaliToDate(vy, vm, d, 23, 59) < min;

  return (
    <div className="cx-picker">
      <button type="button" className={`cx-picker-trigger ${open ? "open" : ""}`} onClick={() => setOpen(o => !o)}>
        <CalendarDays size={16} />
        <span className={value ? "" : "ph"}>{value ? `${fmtDate(value.toISOString())}${withTime ? "، " + fmtTime(value.toISOString()) : ""}` : placeholder}</span>
      </button>
      {open && (
        <div className="cx-picker-panel">
          <div className="cx-picker-head">
            <button type="button" className="cx-icon-btn" onClick={() => step(1)} aria-label="ماه بعد"><ChevronRight size={17} /></button>
            <b>{JALALI_MONTHS[vm - 1]} {fa(vy)}</b>
            <button type="button" className="cx-icon-btn" onClick={() => step(-1)} aria-label="ماه قبل"><ChevronLeft size={17} /></button>
          </div>
          <div className="cx-picker-grid head">{WEEKDAYS_SHORT.map(w => <span key={w}>{w}</span>)}</div>
          <div className="cx-picker-grid">
            {Array.from({ length: first }).map((_, i) => <span key={"e" + i} />)}
            {Array.from({ length: len }).map((_, i) => {
              const d = i + 1; const dt = jalaliToDate(vy, vm, d);
              const sel = !!value && sameDay(dt, value); const tod = sameDay(dt, today);
              return <button type="button" key={d} disabled={isDisabled(d)} className={`${sel ? "sel" : ""} ${tod ? "today" : ""}`} onClick={() => pick(d)}>{fa(d)}</button>;
            })}
          </div>
          <div className="cx-picker-quick">
            <button type="button" onClick={() => quick(0)}>امروز</button>
            <button type="button" onClick={() => quick(1)}>فردا</button>
            <button type="button" onClick={() => quick(7)}>هفته بعد</button>
          </div>
          {withTime && (
            <div className="cx-picker-time">
              <span>ساعت</span>
              <input type="time" value={value ? `${p2(value.getHours())}:${p2(value.getMinutes())}` : "09:00"} onChange={e => setTime(e.target.value)} />
              <button type="button" className="cx-btn sm primary" onClick={() => setOpen(false)}>تأیید</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
